// HIPAA-compliant data sanitization for telemetry payloads.
// Ensures no PII (PHI in healthcare context) leaks to external monitoring systems.
//
// SHARED CONTRACT: these patterns and the sanitizeText() ordering must stay
// identical to the server-side mirror in server.mjs (sanitizeText / SAFE_ROUTE_PATTERNS).
// They cannot share a module (TS browser bundle vs plain-JS Node runtime), so any
// change here must be mirrored in server.mjs, and vice versa. The two layers are
// defense-in-depth; if they drift, the weaker layer silently under-redacts.

// Patterns for identifying sensitive data.
export const REDACTION_PATTERNS = {
  JWT: /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g,
  EMAIL: /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi,
  UUID: /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi,
  // Redact 4+ digit numbers (MRNs, SSNs, case IDs). Matches server threshold.
  LONG_NUMBER: /\b\d{4,}\b/g,
  // Full URLs (could embed identifiers in the path or query).
  URL: /https?:\/\/[^\s)]+/g,
  // Query string parameters (could contain user identifiers, search terms, etc.)
  QUERY_PARAM: /[?&][^=\s]+=[^\s&]+/g,
};

// Map of known safe routes (path patterns where IDs are replaced with :id).
export const SAFE_ROUTE_PATTERNS: Array<[RegExp, string]> = [
  [
    /^\/follow-up-scheduling\/direct\/[^/]+\/[^/]+/,
    '/follow-up-scheduling/direct/:tablespace/:athenaPatientId',
  ],
  [/^\/search\/[^/]+/, '/search/:q'],
  [/^\/practices\/edit\/[^/]+/, '/practices/edit/:id'],
  [/^\/practices\/[^/]+/, '/practices/:id'],
  [/^\/providers\/edit\/[^/]+/, '/providers/edit/:id'],
  [/^\/providers\/[^/]+/, '/providers/:id'],
  [/^\/plans\/edit\/[^/]+/, '/plans/edit/:id'],
  [/^\/plans\/[^/]+\/edit-group/, '/plans/:id/edit-group'],
  [/^\/plans\/[^/]+/, '/plans/:id'],
  [/^\/markets\/edit\/[^/]+/, '/markets/edit/:id'],
  [/^\/markets\/[^/]+\/practices/, '/markets/:id/practices'],
  [/^\/markets\/[^/]+/, '/markets/:id'],
];

// Get current route from pathname, with all IDs anonymized.
export const getSafeRoutePattern = (): string => {
  if (typeof window === 'undefined') {
    return 'unknown';
  }

  const pathname = window.location.pathname || '/';

  // Check if pathname matches a known safe pattern.
  const matchedRoute = SAFE_ROUTE_PATTERNS.find(([pattern]) =>
    pattern.test(pathname)
  );

  if (matchedRoute) {
    return matchedRoute[1];
  }

  // Fallback: redact UUIDs and numeric IDs from unknown routes.
  return pathname
    .replace(REDACTION_PATTERNS.UUID, ':id')
    .replace(/\/\d+(?=\/|$)/g, '/:id');
};

// Redact all sensitive patterns from text. Returns the sanitized string or fallback if empty.
export const sanitizeText = (value: string, fallback: string): string => {
  // Ordering mirrors server.mjs sanitizeText — keep the two in sync.
  const sanitized = value
    .replace(REDACTION_PATTERNS.JWT, '[redacted-token]')
    .replace(REDACTION_PATTERNS.EMAIL, '[redacted-email]')
    .replace(REDACTION_PATTERNS.UUID, '[redacted-id]')
    .replace(REDACTION_PATTERNS.LONG_NUMBER, '[redacted-number]')
    .replace(REDACTION_PATTERNS.URL, '[redacted-url]')
    .replace(REDACTION_PATTERNS.QUERY_PARAM, '');

  return sanitized.trim().slice(0, 500) || fallback;
};

// Get the error name, with sanitization applied.
export const getErrorName = (error: unknown): string => {
  if (error instanceof Error && error.name) {
    return sanitizeText(error.name, 'Error');
  }

  if (typeof error === 'object' && error !== null && 'name' in error) {
    return sanitizeText(String(error.name), 'Error');
  }

  return 'Error';
};

// Extract and sanitize the first stack frame from an error.
// Returns null if no stack is available.
export const getSanitizedStackTop = (error: unknown): string | null => {
  if (!(error instanceof Error) || !error.stack) {
    return null;
  }

  const [, firstFrame] = error.stack.split('\n');
  if (!firstFrame) {
    return null;
  }

  return sanitizeText(firstFrame, 'stack unavailable');
};

// Parse a URL and return the safe path (without query string or sensitive parts).
export const getSafeApiUrl = (url: string | undefined): string | undefined => {
  if (!url) {
    return undefined;
  }

  try {
    const parsedUrl = new URL(url, window.location.origin);
    return parsedUrl.pathname
      .replace(REDACTION_PATTERNS.UUID, ':id')
      .replace(/\/\d+(?=\/|$)/g, '/:id');
  } catch {
    // Fallback for malformed URLs: just extract the path before the query string.
    return sanitizeText(url.split('?')[0] || 'api', 'api')
      .replace(REDACTION_PATTERNS.UUID, ':id')
      .replace(/\/\d+(?=\/|$)/g, '/:id');
  }
};

// Redact error message to prevent leaking user data or sensitive context.
export const sanitizeErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return sanitizeText(error.message, 'unknown error');
  }

  if (typeof error === 'string') {
    return sanitizeText(error, 'unknown error');
  }

  return 'unknown error';
};
